import { datadogRum } from '@datadog/browser-rum-slim';

import { isServer } from './window';

export const DOM_MUTATION_GUARD_FEATURE_FLAG = 'seccl-37725-dom-mutation-guard';

const MAX_REPORTS_PER_SIGNATURE = 5;
const PATCHED_METHODS = ['removeChild', 'insertBefore'] as const;
const REACT_OWNED_KEY_PREFIXES = ['__reactFiber$', '__reactContainer$'];
const TRANSLATED_CLASS = /\btranslated-(ltr|rtl)\b/;

type MutatedCall = (typeof PATCHED_METHODS)[number];

let installed = false;
const reportCountBySignature = new Map<string, number>();

const arePatchedMethodsWritable = () =>
  PATCHED_METHODS.every(method => Object.getOwnPropertyDescriptor(Node.prototype, method)?.writable);

const isReactOwned = (node: Node) =>
  Object.keys(node).some(key => REACT_OWNED_KEY_PREFIXES.some(prefix => key.startsWith(prefix)));

const isForeignMutation = (parent: Node, node: Node | null) =>
  !!node && node.parentNode !== parent && isReactOwned(parent) && isReactOwned(node);

const describeParent = (node: Node) => (node.parentNode ? node.parentNode.nodeName : 'detached');

const reportForeignMutation = (call: MutatedCall, target: Node, subject: Node) => {
  const signature = `${call} (${subject.parentNode ? 'moved' : 'detached'})`;
  const reportCount = reportCountBySignature.get(signature) ?? 0;
  if (reportCount >= MAX_REPORTS_PER_SIGNATURE) {
    return;
  }
  reportCountBySignature.set(signature, reportCount + 1);

  datadogRum.addError(new Error(`dom mutation guard triggered: ${signature}`), {
    call,
    subjectParent: describeParent(subject),
    targetNodeName: target.nodeName,
    documentLanguage: document.documentElement?.lang,
    translatedClass: TRANSLATED_CLASS.test(document.documentElement?.className ?? ''),
    fontElementCount: document.getElementsByTagName('font').length,
  });
};

const isLeftoverTranslation = (node: Node) => node.nodeName === 'FONT' && !isReactOwned(node);

/**
 * When React removes a text node that a translator replaced with a copy, the copy is all that is left of that text.
 * It is only removed when it is the single child, so a sibling text node React still owns is never wiped.
 */
const removeSoleLeftoverTranslation = (parent: Node) => {
  const onlyChild = parent.childNodes.length === 1 ? parent.firstChild : null;
  if (onlyChild && isLeftoverTranslation(onlyChild)) {
    parent.removeChild(onlyChild);
  }
};

const findAncestorThatIsChildOf = (parent: Node, descendant: Node) => {
  let current: Node | null = descendant;
  while (current && current.parentNode !== parent) {
    current = current.parentNode;
  }
  return current;
};

/**
 * Stops React crashing when something outside React (browser translation, extensions) has wrapped, moved or
 * detached a node that React later removes or inserts before. Only calls between two React-owned nodes that
 * would otherwise throw `NotFoundError` are changed; every other call goes straight to the native method.
 */
export const installDomMutationGuard = () => {
  if (isServer || installed || !arePatchedMethodsWritable()) {
    return;
  }
  installed = true;

  const nativeRemoveChild = Node.prototype.removeChild;
  const nativeInsertBefore = Node.prototype.insertBefore;

  Node.prototype.removeChild = function (this: Node, ...args: Parameters<typeof nativeRemoveChild>) {
    const [child] = args;
    if (isForeignMutation(this, child)) {
      reportForeignMutation('removeChild', this, child);
      if (child.parentNode) {
        child.parentNode.removeChild(child);
      } else {
        removeSoleLeftoverTranslation(this);
      }
      return child;
    }
    return nativeRemoveChild.apply(this, args);
  } as typeof nativeRemoveChild;

  Node.prototype.insertBefore = function (this: Node, ...args: Parameters<typeof nativeInsertBefore>) {
    const [node, reference] = args;
    if (reference && isForeignMutation(this, reference)) {
      reportForeignMutation('insertBefore', this, reference);
      return nativeInsertBefore.call(this, node, findAncestorThatIsChildOf(this, reference));
    }
    return nativeInsertBefore.apply(this, args);
  } as typeof nativeInsertBefore;
};
